import React, { useEffect, useState } from "react";
import { Badge, Center, Group, Loader, Paper, SimpleGrid, Table, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { getDefaultConfigDevicesByVersion } from "@utils/api-communication/RestApiCli";

interface DeviceEntry {
  uuid: string;
  alias?: string;
  version?: string;
  puiVersion?: string;
  lastAccess?: string;
  applied: boolean;
}

interface Props {
  // Accepted for callers but intentionally not used: grouping is by
  // (version, puiVersion) only, never hardware model.
  hardwareModel?: string;
  puiVersion: string;
  version: string;
}

const DeviceTable: React.FC<{ devices: DeviceEntry[]; onOpen: (uuid: string) => void }> = ({
  devices,
  onOpen,
}) => {
  if (devices.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="xs">
        None
      </Text>
    );
  }
  return (
    <Table verticalSpacing="xs" horizontalSpacing="sm" striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>UUID</Table.Th>
          <Table.Th>Alias</Table.Th>
          <Table.Th>Last Access</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {devices.map((d) => (
          <Table.Tr
            key={d.uuid}
            onClick={() => onOpen(d.uuid)}
            style={{ cursor: "pointer" }}
            title="Open this device's product status"
          >
            <Table.Td>
              <Text size="xs" c="blue" style={{ wordBreak: "break-all" }}>
                {d.uuid}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs">{d.alias || "-"}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs" c="dimmed">
                {d.lastAccess ? new Date(d.lastAccess).toLocaleString() : "-"}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};

// Normalize puiVersion to its 3-part prefix so a prop with more than 3
// segments (e.g. "1.2.3.4") matches the backend's group key of "1.2.3".
const threePartPui = (full: string | undefined | null): string => {
  if (!full) return "";
  return full.split(".").slice(0, 3).join(".");
};

const DefaultConfigDevices: React.FC<Props> = ({ puiVersion, version }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [applied, setApplied] = useState<DeviceEntry[]>([]);
  const [notApplied, setNotApplied] = useState<DeviceEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    getDefaultConfigDevicesByVersion(
      { puiVersion: threePartPui(puiVersion), version },
      (resp) => {
        if (cancelled) return;
        const data = resp?.data ?? {};
        setApplied(Array.isArray(data.applied) ? data.applied : []);
        setNotApplied(Array.isArray(data.notApplied) ? data.notApplied : []);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [puiVersion, version]);

  if (loading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Text size="sm" c="red" py="xs">
        Failed to load devices for this group.
      </Text>
    );
  }

  // Both pages are admin-only (RoleRoute), so every viewer here can open a
  // product status page.
  const openProductStatus = (uuid: string) =>
    navigate(`/product-status?pid=${encodeURIComponent(uuid)}`, {
      state: { from: "/dconfigs", fromLabel: "Default Configs" },
    });

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
      <Paper withBorder radius="sm" p="sm">
        <Group gap="xs" mb="xs">
          <Badge color="green" variant="light">
            Applied
          </Badge>
          <Text size="sm" c="dimmed">
            {applied.length} device{applied.length === 1 ? "" : "s"}
          </Text>
        </Group>
        <DeviceTable devices={applied} onOpen={openProductStatus} />
      </Paper>
      <Paper withBorder radius="sm" p="sm">
        <Group gap="xs" mb="xs">
          <Badge color="yellow" variant="light">
            Not yet applied
          </Badge>
          <Text size="sm" c="dimmed">
            {notApplied.length} device{notApplied.length === 1 ? "" : "s"} in this group
          </Text>
        </Group>
        <DeviceTable devices={notApplied} onOpen={openProductStatus} />
      </Paper>
    </SimpleGrid>
  );
};

export default DefaultConfigDevices;
export type { DeviceEntry };
